















































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';

import _map from 'lodash.map';

import TrackSelectModal from '@/components/TrackSelectModal.vue';

import { toSeconds, toDateTime } from '@/utils/timeFormat';
import { generateTagId, generateTrackId } from '@/utils/makeId';
import { colors, colorBrightness, trackColor } from '@/utils/tagParser';
import { incrementLast } from '@/utils/text';

@Component
export default class TrackModal extends Vue {
  @Prop() readonly mode!: string;
  @Prop() readonly video!: any;
  @Prop() readonly tracks!: any[];
  @Prop() readonly tags!: any[];
  @Prop() readonly track!: any;
  @Prop() readonly duration!: number;

  default_track_color: string = process.env.VUE_APP_DEFAULT_TRACK_COLOR;
  default_tag_duration: Date = toDateTime(
    this.track.tag_duration || this.video.settings.default_tag_duration
  );
  default_tag_lag_time: Date = toDateTime(
    this.track.tag_lag_time || this.video.settings.default_tag_lag_time
  );

  active: any = {
    tracks: [],
  };

  max_time: Date = toDateTime(this.duration);

  colors: any[] = colors;
  max_order: number = 1;
  track_instance: any = {
    id: '',
    name: '',
    color: '',
    order: 1,
    tag_duration: this.default_tag_duration,
    tag_lag_time: this.default_tag_lag_time,
    note: '',
  };

  mounted() {
    if (this.mode === 'edit') {
      this.track_instance = {
        ...this.track,
        tag_duration: this.default_tag_duration,
        tag_lag_time: this.default_tag_lag_time,
      };
      this.max_order = this.tracks.length - 1;
    } else {
      const track = this.generateNewTrack();
      this.track_instance.id = track.id;
      this.track_instance.color = track.color;
      this.track_instance.order = track.order;
      this.max_order = this.tracks.length;
    }
  }

  generateNewTrack() {
    const id = generateTrackId(this.tracks);
    return {
      id: `track_${id}`,
      color: trackColor(id),
      order: this.tracks.length,
    };
  }

  get colorsHex() {
    return _map(colors, 'hex');
  }

  remove() {
    this.$buefy.dialog.confirm({
      title: 'Deleting Track',
      message:
        'Are you sure you want to <b>delete</b> this track and the associated tags? This action cannot be undone.',
      confirmText: 'Delete Track',
      type: 'is-danger',
      hasIcon: true,
      onConfirm: () => {
        const index = this.tracks.findIndex(
          (track) => track.id === this.track.id
        );

        if (index > -1) {
          this.tracks.splice(index, 1);
        }

        if (index !== this.max_order) {
          this.balanceOrder();
        }

        let i = this.tags.length;
        while (i--) {
          const track_index = this.tags[i].track.indexOf(this.track.id);

          if (track_index > -1) {
            this.tags[i].track.splice(track_index, 1);
            if (this.tags[i].track.length === 0) {
              this.tags.splice(i, 1);
            }
          }
        }

        this.$buefy.toast.open('Track and tags deleted.');
        this.$emit('remove', this.track);
        this.$emit('close');
      },
    });
  }

  removeTags() {
    this.$buefy.dialog.confirm({
      title: 'Deleting Tags',
      message:
        'Are you sure you want to <b>delete</b> all the track tags? This action cannot be undone.',
      confirmText: 'Delete Tags',
      type: 'is-danger',
      hasIcon: true,
      onConfirm: () => {
        let i = this.tags.length;
        while (i--) {
          if (this.track.id === 'trackless') {
            if (!this.tags[i].track || this.tags[i].track.length === 0) {
              this.tags.splice(i, 1);
            }
          } else {
            const track_index = this.tags[i].track.indexOf(this.track.id);

            if (track_index > -1) {
              this.tags[i].track.splice(track_index, 1);
              if (this.tags[i].track.length === 0) {
                this.tags.splice(i, 1);
              }
            }
          }
        }

        this.$buefy.toast.open('Tags deleted.');
        this.$emit('remove', this.track);
        this.$emit('close');
      },
    });
  }

  validate() {
    this.track_instance.name = (this.track_instance.name || '').trim();

    if (!this.track_instance.name) {
      this.$buefy.toast.open({
        message: 'Track name is required.',
        type: 'is-warning',
      });
      return;
    }

    if (/^\d+$/.test(this.track_instance.name)) {
      this.$buefy.toast.open({
        message: 'Track name must not be all numbers.',
        type: 'is-warning',
      });
      return;
    }

    if (!this.track_instance.color) {
      this.$buefy.toast.open({
        message: 'Track color is required.',
        type: 'is-warning',
      });
      return;
    } else {
      this.track_instance.color = this.track_instance.color.toLowerCase();
    }

    if (!/^#([0-9a-f]{3}){1,2}$/.test(this.track_instance.color)) {
      this.$buefy.toast.open({
        message: 'Track color must be a valid hex code.',
        type: 'is-warning',
      });
      return;
    }

    if (colorBrightness(this.track_instance.color) > 230) {
      this.$buefy.toast.open({
        message: 'Track color too light, choose a darker color.',
        type: 'is-warning',
      });
      return;
    }

    if (this.track_instance.id !== 'trackless') {
      if (
        !/^\d+$/.test(this.track_instance.order) ||
        this.track_instance.order < 1 ||
        this.track_instance.order > this.max_order
      ) {
        this.$buefy.toast.open({
          message: `Track order must be between 1 and ${this.max_order}.`,
          type: 'is-warning',
        });
        return;
      }
    }

    return true;
  }

  format() {
    const tag_duration_seconds = toSeconds(
      `${this.track_instance.tag_duration.getUTCHours()}:${this.track_instance.tag_duration.getUTCMinutes()}:${this.track_instance.tag_duration.getUTCSeconds()}`
    );

    const tag_lag_time_seconds = toSeconds(
      `${this.track_instance.tag_lag_time.getUTCHours()}:${this.track_instance.tag_lag_time.getUTCMinutes()}:${this.track_instance.tag_lag_time.getUTCSeconds()}`
    );

    return {
      tag_duration_seconds,
      tag_lag_time_seconds,
    };
  }

  balanceOrder(prev_index?: number) {
    if (typeof prev_index !== 'undefined') {
      this.tracks.splice(
        this.track_instance.order,
        0,
        this.tracks.splice(prev_index, 1)[0]
      );
    }

    for (let i = 1; i < this.tracks.length; i++) {
      this.tracks[i].order = i;
    }
  }

  create() {
    if (!this.validate()) {
      return;
    }

    const { tag_duration_seconds, tag_lag_time_seconds } = this.format();

    this.track_instance.tag_duration = tag_duration_seconds;
    this.track_instance.tag_lag_time = tag_lag_time_seconds;

    this.tracks.push(this.track_instance);

    if (this.track_instance.order !== this.max_order) {
      this.balanceOrder(this.max_order);
    }

    this.$emit('create', this.track_instance);
    this.$emit('close');
  }

  duplicate(duplicate_tags?: boolean) {
    let message =
      'Are you sure you want to <b>duplicate</b> this track and tags?';

    if (duplicate_tags) {
      message += ' This will create <b>exact copies</b> of the <b>tags</b>.';
    } else {
      message +=
        ' This is a soft duplicate where <b>tags</b> are only <b>copied by reference</b> to the original. Changes to the original tags will be reflected.';
    }

    this.$buefy.dialog.confirm({
      title: 'Duplicating Track',
      message,
      confirmText: 'Duplicate Track',
      type: 'is-primary',
      hasIcon: true,
      onConfirm: () => {
        if (!this.validate()) {
          return;
        }

        const index = this.tracks.findIndex(
          (track) => track.id === this.track.id
        );

        const track: any = this.generateNewTrack();

        track.name = this.track_instance.name;
        track.color = this.track_instance.color;

        const { tag_duration_seconds, tag_lag_time_seconds } = this.format();

        track.tag_duration = tag_duration_seconds;
        track.tag_lag_time = tag_lag_time_seconds;

        if (track.name === this.tracks[index].name) {
          track.name = incrementLast(track.name, true);
        }

        this.tracks.push(track);
        this.track_instance.order++;
        this.balanceOrder(this.max_order + 1);

        let i = this.tags.length;
        while (i--) {
          if (this.track.id === 'trackless') {
            if (!this.tags[i].track || this.tags[i].track.length === 0) {
              if (duplicate_tags) {
                this.tags.push({
                  ...this.tags[i],
                  id: `tag_${generateTagId(this.tags)}`,
                  track: [track.id],
                });
              } else {
                this.tags[i].track.push(track.id);
              }
            }
          } else {
            const track_index = this.tags[i].track.indexOf(this.track.id);
            if (track_index > -1) {
              if (duplicate_tags) {
                this.tags.push({
                  ...this.tags[i],
                  id: `tag_${generateTagId(this.tags)}`,
                  track: [track.id],
                });
              } else {
                this.tags[i].track.push(track.id);
              }
            }
          }
        }

        this.$buefy.toast.open('Track and tags duplicated.');
        this.$emit('duplicate', this.track);
        this.$emit('close');
      },
    });
  }

  cloneTags(duplicate_tags?: boolean) {
    this.$buefy.modal.open({
      parent: this,
      component: TrackSelectModal,
      hasModalCard: true,
      canCancel: ['escape', 'outside'],
      props: {
        tracks: this.tracks.filter(
          (track) => ['trackless', this.track.id].indexOf(track.id) === -1
        ),
        active: this.active,
      },
      events: {
        done: (data: any) => {
          this.active.tracks = data;

          if (!this.active.tracks.length) {
            this.$buefy.toast.open({
              message: 'Select at least one track to clone tags.',
              type: 'is-warning',
            });
            return;
          }

          let message =
            'Are you sure you want to <b>clone</b> tags to the selected tracks?';

          if (duplicate_tags) {
            message +=
              ' This will create <b>exact copies</b> of the <b>tags</b>.';
          } else {
            message +=
              ' This is a soft clone where <b>tags</b> are only <b>copied by reference</b> to the original. Changes to the original tags will be reflected.';
          }

          this.$buefy.dialog.confirm({
            title: 'Cloning Tags',
            message,
            confirmText: 'Clone Tags',
            type: 'is-primary',
            hasIcon: true,
            onConfirm: () => {
              let i = this.tags.length;
              while (i--) {
                if (this.track.id === 'trackless') {
                  if (!this.tags[i].track || this.tags[i].track.length === 0) {
                    for (const track of this.active.tracks) {
                      if (duplicate_tags) {
                        this.tags.push({
                          ...this.tags[i],
                          id: `tag_${generateTagId(this.tags)}`,
                          track: [track.id],
                        });
                      } else {
                        if (this.tags[i].track.indexOf(track.id) === -1) {
                          this.tags[i].track.push(track.id);
                        }
                      }
                    }
                  }
                } else {
                  const track_index = this.tags[i].track.indexOf(this.track.id);
                  if (track_index > -1) {
                    for (const track of this.active.tracks) {
                      if (duplicate_tags) {
                        this.tags.push({
                          ...this.tags[i],
                          id: `tag_${generateTagId(this.tags)}`,
                          track: [track.id],
                        });
                      } else {
                        if (this.tags[i].track.indexOf(track.id) === -1) {
                          this.tags[i].track.push(track.id);
                        }
                      }
                    }
                  }
                }
              }

              this.$buefy.toast.open('Tags cloned.');
              this.$emit('clone', this.track);
              this.$emit('close');
            },
          });
        },
      },
    });
  }

  save() {
    if (!this.validate()) {
      return;
    }

    const { tag_duration_seconds, tag_lag_time_seconds } = this.format();

    this.track_instance.tag_duration = tag_duration_seconds;
    this.track_instance.tag_lag_time = tag_lag_time_seconds;

    const index = this.tracks.findIndex((track) => track.id === this.track.id);

    if (this.track_instance.name !== this.tracks[index].name) {
      this.tracks[index].name = this.track_instance.name;
    }

    if (this.track_instance.color !== this.tracks[index].color) {
      this.tracks[index].color = this.track_instance.color;
    }

    if (this.track_instance.order !== this.tracks[index].order) {
      this.balanceOrder(this.tracks[index].order);
    }

    if (this.track_instance.tag_duration !== this.tracks[index].tag_duration) {
      this.tracks[index].tag_duration = this.track_instance.tag_duration;
    }

    if (this.track_instance.tag_lag_time !== this.tracks[index].tag_lag_time) {
      this.tracks[index].tag_lag_time = this.track_instance.tag_lag_time;
    }

    if (this.track_instance.note !== this.tracks[index].note) {
      this.tracks[index].note = this.track_instance.note;
    }

    this.$emit('save', this.track_instance);
    this.$emit('close');
  }
}
